import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import HandoverProgress from "../components/HandoverProgress";
import { Wrench } from "lucide-react";
import { toast } from "sonner";

const MODES = [
    { id: "scrutiny", label: "Scrutiny — agent still in place" },
    { id: "self_managed", label: "Self-managed — the company runs the work" },
    { id: "transition", label: "Transition — handover or switching agent" },
];

function pounds(pence) {
    if (pence === null || pence === undefined) return "—";
    return `£${(Math.abs(pence) / 100).toFixed(2)}`;
}

/**
 * How the estate is run. Shared spine; the mode only changes which tools are emphasised.
 * Demands here are estate totals. Named household arrears are not shown.
 */
export default function RunEstate() {
    const { user } = useAuth();
    const isAdmin = user?.role === "estate_admin";
    const [settings, setSettings] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [demands, setDemands] = useState([]);
    const [deadlines, setDeadlines] = useState([]);
    const [storage, setStorage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [demandForm, setDemandForm] = useState({
        label: "2026/27 first demand", total: "", issued_on: "2026-04-01", due_on: "2026-04-15",
    });
    const [supplierForm, setSupplierForm] = useState({
        name: "", service_type: "grounds", contract_end: "", renewal_notice_days: "90",
    });

    const load = async () => {
        try {
            const [s, sup, dem, g, st] = await Promise.all([
                api.get("/estate/settings"),
                api.get("/finance/suppliers").catch(() => ({ data: [] })),
                api.get("/finance/demands").catch(() => ({ data: { demands: [] } })),
                api.get("/guidance/deadlines").catch(() => ({ data: [] })),
                api.get("/storage/status").catch(() => ({ data: null })),
            ]);
            setSettings(s.data);
            setSuppliers(Array.isArray(sup.data) ? sup.data : []);
            setDemands(dem.data?.demands || []);
            setDeadlines(g.data?.deadlines || []);
            setStorage(st.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load how the estate is run");
        }
    };

    useEffect(() => { load(); }, []);

    const setMode = async (operating_mode) => {
        setBusy(true);
        try {
            const res = await api.put("/estate/settings", { operating_mode });
            setSettings(res.data);
            toast.success("Operating mode updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update mode");
        } finally {
            setBusy(false);
        }
    };

    const addDemand = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/finance/demands", {
                label: demandForm.label,
                total_pence: Math.round(parseFloat(demandForm.total) * 100),
                issued_on: demandForm.issued_on,
                due_on: demandForm.due_on || null,
            });
            toast.success("Estate demand recorded (no named households)");
            setDemandForm({ ...demandForm, total: "" });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not record demand");
        } finally {
            setBusy(false);
        }
    };

    const addSupplier = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/finance/suppliers", {
                name: supplierForm.name,
                service_type: supplierForm.service_type,
                contract_end: supplierForm.contract_end || null,
                renewal_notice_days: supplierForm.renewal_notice_days
                    ? parseInt(supplierForm.renewal_notice_days, 10)
                    : null,
            });
            toast.success("Supplier recorded");
            setSupplierForm({ ...supplierForm, name: "" });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not record supplier");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="run-estate-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <Wrench size={14} /> How the estate is run
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">Run the estate</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                The same records whether an agent is retained, you self-manage, or you are in handover.
                Named residents&apos; arrears are never listed here.
            </p>

            <section className="mt-8">
                <h2 className="font-heading text-lg font-semibold">Operating mode</h2>
                <div className="mt-3 grid gap-2" data-testid="operating-mode">
                    {MODES.map((m) => (
                        <button
                            key={m.id}
                            type="button"
                            disabled={!isAdmin || busy}
                            onClick={() => setMode(m.id)}
                            className={`text-left rounded-lg border px-4 py-3 text-sm ${
                                settings?.operating_mode === m.id
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 bg-white"
                            }`}
                            data-testid={`mode-${m.id}`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
                {settings?.operating_mode === "transition" && (
                    <p className="text-sm mt-3">
                        <Link to="/handover" className="underline" data-testid="handover-from-run">
                            Open the handover records demand
                        </Link>
                    </p>
                )}
            </section>

            <HandoverProgress isAdmin={isAdmin} />

            <section className="mt-8">
                <h2 className="font-heading text-lg font-semibold">Estate demands</h2>
                <p className="text-xs text-slate-500 mt-1">Totals only. Keep paying what is demanded while any challenge is separate.</p>
                <ul className="mt-3 space-y-2">
                    {demands.map((d) => (
                        <li key={d.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`demand-${d.id}`}>
                            <div className="font-medium">{d.label}</div>
                            <div className="text-sm tabular-nums mt-1">{pounds(d.total_pence)} · issued {d.issued_on}{d.due_on ? ` · due ${d.due_on}` : ""}</div>
                        </li>
                    ))}
                    {demands.length === 0 && <li className="text-sm text-slate-500">No estate-level demand recorded yet.</li>}
                </ul>
                {isAdmin && (
                    <form onSubmit={addDemand} className="mt-4 grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-4">
                        <div className="sm:col-span-2">
                            <Label>Label</Label>
                            <Input required value={demandForm.label} onChange={(e) => setDemandForm({ ...demandForm, label: e.target.value })} />
                        </div>
                        <div>
                            <Label>Total (£)</Label>
                            <Input required inputMode="decimal" value={demandForm.total} onChange={(e) => setDemandForm({ ...demandForm, total: e.target.value })} data-testid="demand-total-input" />
                        </div>
                        <div>
                            <Label>Issued</Label>
                            <Input type="date" required value={demandForm.issued_on} onChange={(e) => setDemandForm({ ...demandForm, issued_on: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2">
                            <Button type="submit" disabled={busy} className="bg-slate-900 text-white h-9 text-xs">Record estate demand</Button>
                        </div>
                    </form>
                )}
            </section>

            <section className="mt-8">
                <h2 className="font-heading text-lg font-semibold">Contractors and insurance</h2>
                <ul className="mt-3 space-y-2">
                    {suppliers.map((s) => (
                        <li key={s.id} className="rounded-lg border border-slate-200 bg-white p-4 text-sm" data-testid={`supplier-${s.id}`}>
                            <div className="font-medium">{s.name}</div>
                            <div className="text-slate-500">{s.service_type || "unspecified"}{s.contract_end ? ` · ends ${s.contract_end}` : ""}</div>
                        </li>
                    ))}
                    {suppliers.length === 0 && <li className="text-sm text-slate-500">No suppliers recorded yet.</li>}
                </ul>
                {isAdmin && (
                    <form onSubmit={addSupplier} className="mt-4 grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-4">
                        <div>
                            <Label>Name</Label>
                            <Input required value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} data-testid="supplier-name-input" />
                        </div>
                        <div>
                            <Label>Service</Label>
                            <Input value={supplierForm.service_type} onChange={(e) => setSupplierForm({ ...supplierForm, service_type: e.target.value })} />
                        </div>
                        <div>
                            <Label>Contract end</Label>
                            <Input type="date" value={supplierForm.contract_end} onChange={(e) => setSupplierForm({ ...supplierForm, contract_end: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2">
                            <Button type="submit" disabled={busy} className="bg-slate-900 text-white h-9 text-xs">Add supplier</Button>
                        </div>
                    </form>
                )}
            </section>

            <section className="mt-8">
                <h2 className="font-heading text-lg font-semibold">Company filing dates we can compute</h2>
                <p className="text-xs text-slate-500 mt-1">Only shown when this estate has recorded the underlying date. Confirm on the company record.</p>
                <ul className="mt-3 space-y-2">
                    {(Array.isArray(deadlines) ? deadlines : []).map((d) => (
                        <li key={d.key} className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
                            {d.title} · {d.due_date}
                        </li>
                    ))}
                    {(!deadlines || deadlines.length === 0) && (
                        <li className="text-sm text-slate-500">No filing dates computed yet. Set them under Set up estate / guidance dates.</li>
                    )}
                </ul>
            </section>

            {storage && (
                <p className="mt-8 text-xs text-slate-500" data-testid="storage-status">
                    Document storage: {storage.active || storage.configured} (not Emergent).
                </p>
            )}
        </div>
    );
}
